import { type MinorUnits, assertMinorUnits } from "./money.js";

/**
 * Invoice lifecycle.
 *
 * ── THE CENTRAL DESIGN DECISION ──────────────────────────────────────────────
 *
 * There are only TWO things an actor can *do* to an invoice's status: ISSUE it,
 * or VOID it. Everything else — PARTIALLY_PAID, PAID — is DERIVED from the sum
 * of the payment ledger and is never stored as an independently settable field.
 *
 * The alternative (a status column someone can set to PAID) allows an invoice
 * marked PAID with no payments behind it, or marked ISSUED while fully paid.
 * Those two facts then disagree forever and no amount of later care repairs it.
 * Deriving means the status cannot contradict the money, because it IS the
 * money, read a different way.
 *
 * So the stored column holds only the *settlement* state — ISSUED or VOIDED (or
 * DRAFT before issuance) — and the payment state is computed on read.
 */

export const INVOICE_STATUSES = [
  "DRAFT",
  "ISSUED",
  "PARTIALLY_PAID",
  "PAID",
  "VOIDED",
] as const;

export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];

/**
 * What is actually persisted. Note the absence of PARTIALLY_PAID and PAID —
 * they are not storable, by construction.
 */
export const INVOICE_SETTLEMENT_STATES = ["DRAFT", "ISSUED", "VOIDED"] as const;
export type InvoiceSettlementState = (typeof INVOICE_SETTLEMENT_STATES)[number];

export type Bilingual = { readonly ar: string; readonly en: string };

export interface InvoiceStatusDefinition {
  readonly labelAr: string;
  readonly labelEn: string;
  readonly descriptionAr: string;
  readonly descriptionEn: string;
  /** Matches the Badge component's tone union. */
  readonly tone: "neutral" | "info" | "success" | "warning" | "danger";
  /** True once the invoice is a legal document and its contents are frozen. */
  readonly immutable: boolean;
  /** True when no further status change is possible. */
  readonly terminal: boolean;
}

export const INVOICE_STATUS_DEFINITIONS: Readonly<Record<InvoiceStatus, InvoiceStatusDefinition>> = {
  DRAFT: {
    labelAr: "مسودة",
    labelEn: "Draft",
    descriptionAr: "لم تُصدر بعد — يمكن التعديل أو الحذف.",
    descriptionEn: "Not yet issued. Can still be edited or deleted.",
    tone: "neutral",
    immutable: false,
    terminal: false,
  },
  ISSUED: {
    labelAr: "صادرة",
    labelEn: "Issued",
    descriptionAr: "مستند ضريبي نهائي. لا يمكن تعديله — يُلغى ويُعاد إصداره.",
    descriptionEn: "A final tax document. It cannot be edited — void and reissue instead.",
    tone: "info",
    immutable: true,
    terminal: false,
  },
  PARTIALLY_PAID: {
    labelAr: "مدفوعة جزئيًا",
    labelEn: "Partially paid",
    descriptionAr: "سُدّد جزء من المبلغ ويتبقّى رصيد مستحق.",
    descriptionEn: "Some of the amount has been paid; a balance remains.",
    tone: "warning",
    immutable: true,
    terminal: false,
  },
  PAID: {
    labelAr: "مدفوعة",
    labelEn: "Paid",
    descriptionAr: "سُدّد كامل المبلغ.",
    descriptionEn: "Settled in full.",
    tone: "success",
    immutable: true,
    terminal: false,
  },
  VOIDED: {
    labelAr: "ملغاة",
    labelEn: "Voided",
    descriptionAr: "أُلغيت مع بقاء سجلها. لا تُحتسب ضمن الإيرادات.",
    descriptionEn: "Cancelled, but retained on record. Excluded from revenue.",
    tone: "danger",
    immutable: true,
    terminal: true,
  },
};

/**
 * Statuses whose totals count toward revenue and receivables.
 *
 * DRAFT is excluded because it is not yet a document; VOIDED is excluded
 * because it has been withdrawn. Everything issued counts — including PAID,
 * which is revenue that happens to have been collected.
 */
export const REVENUE_BEARING_STATUSES: readonly InvoiceStatus[] = [
  "ISSUED",
  "PARTIALLY_PAID",
  "PAID",
];

/** Statuses that still carry an outstanding balance. */
export const RECEIVABLE_STATUSES: readonly InvoiceStatus[] = ["ISSUED", "PARTIALLY_PAID"];

export function isRevenueBearing(status: InvoiceStatus): boolean {
  return REVENUE_BEARING_STATUSES.includes(status);
}

/**
 * Derives the visible status from what is stored plus the payment ledger.
 *
 * VOIDED wins over any payment state: a voided invoice that had been paid is
 * still voided — the refund is a separate ledger entry, not a status.
 */
export function deriveInvoiceStatus(
  settlement: InvoiceSettlementState,
  totalMinor: MinorUnits,
  amountPaidMinor: MinorUnits,
): InvoiceStatus {
  assertMinorUnits(totalMinor, "totalMinor");
  assertMinorUnits(amountPaidMinor, "amountPaidMinor");

  if (settlement === "DRAFT") return "DRAFT";
  if (settlement === "VOIDED") return "VOIDED";

  // Settled is checked FIRST, before "nothing paid".
  //
  // The order matters for a zero-total invoice (a full discount, say). Asking
  // "has anything been paid?" first would answer no and report ISSUED — leaving
  // it in receivables forever with a balance of zero, unclearable, because
  // refusePayment rejects every positive amount as exceeding the balance.
  //
  // >= rather than === also means an overpayment (which the API refuses, but a
  // manual database edit could still produce) reads as PAID instead of falling
  // through to PARTIALLY_PAID and displaying a negative balance as "due".
  if (amountPaidMinor >= totalMinor) return "PAID";

  if (amountPaidMinor <= 0) return "ISSUED";

  return "PARTIALLY_PAID";
}

/* ── Refusals ─────────────────────────────────────────────────────────────── */

export type InvoiceRefusalCode =
  | "ALREADY_ISSUED"
  | "NOT_ISSUED"
  | "ALREADY_VOIDED"
  | "IMMUTABLE"
  | "NO_LINES"
  | "VOID_REASON_REQUIRED"
  | "PAYMENT_ON_UNISSUED"
  | "PAYMENT_ON_VOIDED"
  | "PAYMENT_EXCEEDS_BALANCE"
  | "PAYMENT_NOT_POSITIVE";

export interface InvoiceRefusal {
  readonly code: InvoiceRefusalCode;
  readonly messageAr: string;
  readonly messageEn: string;
}

const REFUSALS: Readonly<Record<InvoiceRefusalCode, Bilingual>> = {
  ALREADY_ISSUED: {
    ar: "هذه الفاتورة صادرة بالفعل.",
    en: "This invoice has already been issued.",
  },
  NOT_ISSUED: {
    ar: "لم تُصدر هذه الفاتورة بعد.",
    en: "This invoice has not been issued yet.",
  },
  ALREADY_VOIDED: {
    ar: "هذه الفاتورة ملغاة بالفعل.",
    en: "This invoice is already voided.",
  },
  IMMUTABLE: {
    ar: "لا يمكن تعديل فاتورة صادرة. ألغِها وأصدر فاتورة جديدة.",
    en: "An issued invoice cannot be edited. Void it and issue a new one.",
  },
  NO_LINES: {
    ar: "لا يمكن إصدار فاتورة بلا بنود.",
    en: "An invoice with no lines cannot be issued.",
  },
  VOID_REASON_REQUIRED: {
    ar: "يجب ذكر سبب الإلغاء.",
    en: "A reason is required in order to void an invoice.",
  },
  PAYMENT_ON_UNISSUED: {
    ar: "لا يمكن تسجيل دفعة على فاتورة غير صادرة.",
    en: "A payment cannot be recorded against an invoice that has not been issued.",
  },
  PAYMENT_ON_VOIDED: {
    ar: "لا يمكن تسجيل دفعة على فاتورة ملغاة.",
    en: "A payment cannot be recorded against a voided invoice.",
  },
  PAYMENT_EXCEEDS_BALANCE: {
    ar: "المبلغ يتجاوز الرصيد المستحق.",
    en: "The amount is greater than the balance still due.",
  },
  PAYMENT_NOT_POSITIVE: {
    ar: "يجب أن يكون مبلغ الدفعة أكبر من صفر.",
    en: "A payment must be greater than zero.",
  },
};

function refuse(code: InvoiceRefusalCode): InvoiceRefusal {
  const copy = REFUSALS[code];
  return { code, messageAr: copy.ar, messageEn: copy.en };
}

/* ── Guards ───────────────────────────────────────────────────────────────── */

/** May this invoice's CONTENT (lines, customer, dates) still be changed? */
export function refuseEdit(settlement: InvoiceSettlementState): InvoiceRefusal | null {
  if (settlement === "VOIDED") return refuse("ALREADY_VOIDED");
  if (settlement === "ISSUED") return refuse("IMMUTABLE");
  return null;
}

export function refuseIssue(settlement: InvoiceSettlementState, lineCount: number): InvoiceRefusal | null {
  if (settlement === "ISSUED") return refuse("ALREADY_ISSUED");
  if (settlement === "VOIDED") return refuse("ALREADY_VOIDED");
  if (lineCount <= 0) return refuse("NO_LINES");
  return null;
}

/**
 * Voiding is allowed from ISSUED regardless of how much has been paid.
 *
 * Refusing to void a paid invoice sounds prudent but is wrong: invoices are
 * voided precisely *because* something was wrong, and being paid does not make
 * a wrong invoice right. The money is reconciled by a refund entry in the
 * payment ledger, which is a fact, rather than by blocking the correction.
 */
export function refuseVoid(settlement: InvoiceSettlementState, reason: string): InvoiceRefusal | null {
  if (settlement === "DRAFT") return refuse("NOT_ISSUED");
  if (settlement === "VOIDED") return refuse("ALREADY_VOIDED");
  if (reason.trim().length < 3) return refuse("VOID_REASON_REQUIRED");
  return null;
}

/** A draft may be deleted outright; an issued document may never be. */
export function refuseDelete(settlement: InvoiceSettlementState): InvoiceRefusal | null {
  if (settlement === "ISSUED") return refuse("IMMUTABLE");
  if (settlement === "VOIDED") return refuse("ALREADY_VOIDED");
  return null;
}

export function refusePayment(
  settlement: InvoiceSettlementState,
  totalMinor: MinorUnits,
  alreadyPaidMinor: MinorUnits,
  amountMinor: MinorUnits,
): InvoiceRefusal | null {
  assertMinorUnits(totalMinor, "totalMinor");
  assertMinorUnits(alreadyPaidMinor, "alreadyPaidMinor");
  assertMinorUnits(amountMinor, "amountMinor");

  if (settlement === "DRAFT") return refuse("PAYMENT_ON_UNISSUED");
  if (settlement === "VOIDED") return refuse("PAYMENT_ON_VOIDED");
  if (amountMinor <= 0) return refuse("PAYMENT_NOT_POSITIVE");

  // Overpayment is refused rather than absorbed. Silently accepting it would
  // produce a negative balance that reads as credit the business does not
  // actually owe, and would break the stored-balance CHECK constraint anyway.
  if (alreadyPaidMinor + amountMinor > totalMinor) return refuse("PAYMENT_EXCEEDS_BALANCE");

  return null;
}

/** The transitions the UI may offer, given what is stored. */
export function allowedInvoiceActions(settlement: InvoiceSettlementState): readonly InvoiceAction[] {
  switch (settlement) {
    case "DRAFT":
      return ["issue", "edit", "delete"];
    case "ISSUED":
      return ["void", "record_payment"];
    case "VOIDED":
      return [];
  }
}

export type InvoiceAction = "issue" | "edit" | "delete" | "void" | "record_payment";

/* ── Payment methods ──────────────────────────────────────────────────────── */

export const PAYMENT_METHODS = ["CASH", "CARD", "TRANSFER", "OTHER"] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

export const PAYMENT_METHOD_DEFINITIONS: Readonly<Record<PaymentMethod, Bilingual>> = {
  CASH: { ar: "نقدًا", en: "Cash" },
  CARD: { ar: "شبكة / بطاقة", en: "Card" },
  TRANSFER: { ar: "تحويل بنكي", en: "Bank transfer" },
  OTHER: { ar: "أخرى", en: "Other" },
};

/* ── Canonical view model ─────────────────────────────────────────────────── */

/**
 * The ONE shape every renderer consumes — screen, A4 PDF, 58mm and 80mm
 * thermal. Each of those chooses what to show and how to lay it out; none of
 * them recomputes a number.
 *
 * That constraint is the whole point. The moment a thermal template computes
 * its own VAT because the field it wanted wasn't here, the receipt in the
 * customer's hand can disagree with the PDF in the accountant's folder. So
 * every figure a renderer could conceivably need is precomputed here, in both
 * minor units (for arithmetic) and formatted strings (for display).
 */
/**
 * Owner-controlled invoice PRESENTATION.
 *
 * One contract, consumed by every renderer — the on-screen document, the A4
 * and thermal print views, and the server PDF template — so a setting cannot
 * take effect on one surface and not another.
 *
 * NOTHING HERE CAN CHANGE A NUMBER. An issued invoice's money lives in
 * snapshot columns frozen at issuance; these flags decide only whether an
 * already-frozen value is displayed. A historical invoice reprinted after a
 * settings change shows the same totals it always did.
 */
export interface InvoicePresentation {
  readonly showLogo: boolean;
  readonly logoSize: "small" | "medium" | "large";
  readonly logoAlign: "start" | "center" | "end";
  readonly showBusinessName: boolean;
  readonly showVatNumber: boolean;
  readonly showAddress: boolean;
  readonly showDatetime: boolean;
  readonly showCustomer: boolean;
  readonly showPaymentMethod: boolean;
  readonly showTaxDetail: boolean;
  readonly showNotes: boolean;
  readonly showQr: boolean;
  readonly qrSize: "small" | "medium" | "large";
  readonly qrPosition: "top" | "bottom";
}

/** Renders exactly as the product did before any of this was configurable. */
export const DEFAULT_INVOICE_PRESENTATION: InvoicePresentation = {
  showLogo: true,
  logoSize: "medium",
  logoAlign: "center",
  showBusinessName: true,
  showVatNumber: true,
  showAddress: true,
  showDatetime: true,
  showCustomer: true,
  showPaymentMethod: true,
  showTaxDetail: true,
  showNotes: true,
  showQr: true,
  qrSize: "medium",
  qrPosition: "bottom",
};

/** QR edge length in CSS pixels, shared so print and PDF agree exactly. */
export const QR_PIXEL_SIZE: Record<InvoicePresentation["qrSize"], number> = {
  small: 96,
  medium: 128,
  large: 168,
};

/** Logo max height in CSS pixels. */
export const LOGO_PIXEL_HEIGHT: Record<InvoicePresentation["logoSize"], number> = {
  small: 32,
  medium: 48,
  large: 68,
};

/**
 * Which language a PRINTED invoice is written in.
 *
 * Deliberately separate from the language of the app around it. The person
 * reading the screen and the person holding the receipt are often not the same
 * person: a Saudi laundry may be operated in Arabic by staff who print English
 * invoices for a corporate client, or the reverse. Tying the document to the
 * operator's UI language would make the printed artifact change depending on
 * who happened to be signed in — and would let the browser print and the
 * server PDF of the SAME invoice come out in different languages, which is
 * exactly the drift this contract exists to prevent.
 *
 * "both" is accepted because it is a stored value on existing rows, but no
 * template renders two languages side by side, so it resolves to Arabic — the
 * product default — rather than silently rendering something the owner did not
 * choose. The settings UI does not offer it.
 */
export function resolveDocumentLanguage(
  // Widened past the union on purpose: the worker reads this straight off a
  // TEXT column, so the input really can be any string. Normalising it here is
  // the point — anything unrecognised resolves to the product default rather
  // than reaching a template.
  receiptLanguage: string | null | undefined,
): "ar" | "en" {
  return receiptLanguage === "en" ? "en" : "ar";
}

export interface InvoiceViewModel {
  readonly id: string;
  readonly invoiceRef: string;
  readonly status: InvoiceStatus;
  readonly settlement: InvoiceSettlementState;

  /** Snapshotted at issuance — NOT read from current settings. */
  readonly seller: {
    readonly nameAr: string;
    readonly nameEn: string;
    readonly vatNumber: string | null;
    readonly crNumber: string | null;
    readonly addressAr: string | null;
    readonly addressEn: string | null;
    readonly phone: string | null;
    readonly logoUrl: string | null;
  };

  readonly customer: {
    readonly id: string | null;
    readonly name: string | null;
    readonly mobile: string | null;
    readonly vatNumber: string | null;
    readonly address: string | null;
  };

  readonly orderRef: string | null;
  readonly issuedAt: string | null;
  readonly createdAt: string;
  readonly dueAt: string | null;
  readonly notes: string | null;
  readonly voidReason: string | null;
  readonly voidedAt: string | null;

  readonly lines: readonly InvoiceViewLine[];

  readonly totals: {
    readonly subtotalMinor: MinorUnits;
    readonly discountMinor: MinorUnits;
    readonly taxableBaseMinor: MinorUnits;
    readonly taxMinor: MinorUnits;
    readonly totalMinor: MinorUnits;
    readonly amountPaidMinor: MinorUnits;
    readonly balanceDueMinor: MinorUnits;
  };

  readonly tax: {
    readonly rateBps: number;
    readonly ratePercent: string;
    readonly pricesInclusive: boolean;
    readonly vatEnabled: boolean;
    /** One entry per distinct rate, for a multi-rate VAT summary block. */
    readonly byRate: readonly { readonly rateBps: number; readonly baseMinor: MinorUnits; readonly taxMinor: MinorUnits }[];
  };

  readonly payments: readonly {
    readonly id: string;
    readonly amountMinor: MinorUnits;
    readonly method: PaymentMethod;
    readonly reference: string | null;
    readonly receivedAt: string;
    readonly note: string | null;
  }[];

  /** Base64 TLV. Null while DRAFT — an unissued invoice has no legal QR. */
  readonly zatcaQrPayload: string | null;

  readonly currency: { readonly code: string; readonly labelAr: string; readonly labelEn: string };

  /**
   * True when this document is a *simplified* tax invoice (B2C). A standard
   * tax invoice (B2B, customer has a VAT number) has different field
   * requirements, so renderers need to know which one they are drawing.
   */
  readonly isSimplified: boolean;
}

export interface InvoiceViewLine {
  readonly id: string;
  readonly itemNameAr: string;
  readonly itemNameEn: string;
  readonly serviceNameAr: string;
  readonly serviceNameEn: string;
  readonly quantity: number;
  readonly unitPriceMinor: MinorUnits;
  readonly discountMinor: MinorUnits;
  readonly netMinor: MinorUnits;
  readonly taxRateBps: number;
  readonly taxMinor: MinorUnits;
  readonly lineTotalMinor: MinorUnits;
  readonly notes: string | null;
}

/**
 * A tax invoice is "simplified" when it is issued to a customer without a VAT
 * registration — the ordinary retail case. When the buyer IS registered, the
 * document must carry the buyer's VAT number and becomes a standard invoice.
 */
export function isSimplifiedInvoice(customerVatNumber: string | null | undefined): boolean {
  return !customerVatNumber || customerVatNumber.trim().length === 0;
}

export const INVOICE_DOCUMENT_TITLES = {
  simplified: { ar: "فاتورة ضريبية مبسطة", en: "Simplified Tax Invoice" },
  standard: { ar: "فاتورة ضريبية", en: "Tax Invoice" },
  draft: { ar: "مسودة فاتورة", en: "Draft Invoice" },
  voided: { ar: "فاتورة ملغاة", en: "Voided Invoice" },
} as const;

/** The heading a renderer should print, given status and buyer registration. */
export function invoiceDocumentTitle(status: InvoiceStatus, isSimplified: boolean): Bilingual {
  if (status === "DRAFT") return INVOICE_DOCUMENT_TITLES.draft;
  if (status === "VOIDED") return INVOICE_DOCUMENT_TITLES.voided;
  return isSimplified ? INVOICE_DOCUMENT_TITLES.simplified : INVOICE_DOCUMENT_TITLES.standard;
}
